import { fromPath } from '../../../gardener/sets/mythic';
import { type PixelCanvas, sprite } from '../../../lib';
import { burst, frames3, frames4, muzzle, paint, shot, type Tone, under } from './shared';

/**
 * Permafrost's shots (the Glacier set's gun, ADR-0160 amended): an icicle bolt, a cone of frost out of the muzzle, a
 * shot shattering into shards; and the bit of the Glacier's knock-out (a block of ice, cracked, and its shards).
 * Outline-free: effects are the brightest things on screen. No team keys.
 */

/** Snow behind a bolt per frame: [x, y] of each flake (the far ones dimmer). */
const TRAIL = [
  [
    [2, 7],
    [5, 10],
    [8, 4],
    [10, 11],
  ],
  [
    [1, 9],
    [4, 5],
    [9, 11],
    [10, 4],
  ],
  [
    [3, 4],
    [6, 10],
    [9, 6],
    [0, 8],
  ],
] as const;

/**
 * An icicle flying point first: its point at x 28, its thick broken base back at 12, a white heart, cyan along its top
 * edge and pale grey along its bottom; a streak and flakes of snow trailing it.
 */
const icicle = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const dy = y - 8;
    // The base: broken off unevenly, a row ending a px further than the next.
    const base = 12 + (Math.floor(y) % 2) * 1.2;
    const half = x > 17 ? 3.6 * ((28 - x) / 11) : 3.6;
    if (x > base && x < 28 && Math.abs(dy) < half) {
      const r = dy / half;
      if (Math.abs(r) < 0.4) return x > 14 ? 'w' : '5';
      return r < 0 ? 'c' : x > 20 ? '5' : '4';
    }
    // Its frost trail: a thin streak on its line, flakes round it.
    if (Math.abs(dy) < 0.5 && x > 4 && x <= base) return x > 8 ? '5' : '4';
    for (const [i, [fx, fy]] of (TRAIL[f] ?? TRAIL[0]).entries())
      if (Math.abs(x - fx - 0.5) < 0.5 && Math.abs(y - fy - 0.5) < 0.5) return i % 2 ? '5' : fx > 7 ? 'w' : '4';
    return null;
  });

/** A snowflake at (cx, cy): a white heart, six arms (a plus and its diagonals' tips), `big` with a second ring. */
const flake = (x: number, y: number, cx: number, cy: number, big: boolean): Tone => {
  const [dx, dy] = [Math.floor(x) - cx, Math.floor(y) - cy];
  if (dx === 0 && dy === 0) return 'w';
  const reach = big ? 2 : 1;
  if ((dx === 0 && Math.abs(dy) <= reach) || (dy === 0 && Math.abs(dx) <= reach)) return big ? '5' : 'c';
  if (big && Math.abs(dx) === 2 && Math.abs(dy) === 2) return 'c';
  return null;
};

/** A cone of frost out of the muzzle from a white flash, snowflakes blown off its edge; the cone thins and the flakes fly. */
const frostCone = (f: number) => (c: PixelCanvas) => {
  const reach = [30, 38, 42][f] as number;
  const flakes = [
    [16, 13, 10, 27, 22, 19],
    [24, 10, 17, 30, 32, 17],
    [31, 8, 24, 33, 40, 15],
  ][f] as readonly number[];
  paint(c, (x, y) => {
    for (let k = 0; k + 1 < flakes.length; k += 2) {
      const t = flake(x, y, flakes[k] as number, flakes[k + 1] as number, k === 2);
      if (t) return t;
    }
    if (f === 0) {
      const flash = under(Math.hypot(x - 6, y - 20), [
        ['w', 3.2],
        ['5', 4.6],
      ]);
      if (flash) return flash;
    }
    const t = (x - 4) / reach;
    if (t < (f === 2 ? 0.45 : f === 1 ? 0.2 : 0) || t > 1) return null;
    const half = 1.5 + 9 * t ** 0.8;
    const d = Math.abs(y - 20) / half;
    if (d >= 1) return null;
    // Breaking up: frost in streaks along the cone, more gaps the further and the later.
    const streak = Math.sin((y - 20) * 1.9 + x * 0.25 + f) > 0.55 - t * 0.6 - f * 0.35;
    if (streak && t > 0.35) return null;
    if (f === 2) return d < 0.5 ? '5' : '4';
    return d < 0.32 && t < 0.6 ? 'w' : d < 0.68 ? '5' : t > 0.7 ? '4' : 'c';
  });
};

/** Shards flung out of a burst: [angle, length] each. */
const SHARDS = [
  [0.2, 1],
  [1.25, 0.8],
  [2.1, 1],
  [3.0, 0.75],
  [3.9, 0.95],
  [4.8, 0.8],
  [5.6, 1],
] as const;

/** A shard of ice at `r` out along angle `a`, `len` long: lit along its leading side, a point at its far end. */
const shardAt = (x: number, y: number, a: number, r: number, len: number, half: number): Tone => {
  const [ux, uy] = [Math.cos(a), Math.sin(a)];
  const [dx, dy] = [x - 22, y - 22];
  const along = dx * ux + dy * uy - r;
  const across = -dx * uy + dy * ux;
  if (along < 0 || along > len) return null;
  const w = half * (1 - along / len);
  if (Math.abs(across) >= Math.max(0.5, w)) return null;
  return across < 0 ? 'w' : along < len * 0.5 ? '5' : 'c';
};

/** The shot shatters: a white star of crystal, its shards flung out and thinning to glints. */
const shatter = (f: number) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    const [dx, dy] = [x - 22, y - 22];
    const d = Math.hypot(dx, dy);
    if (f === 0) {
      if (d < 2.6) return 'w';
      // Six spikes of crystal out of a white heart.
      const a = Math.atan2(dy, dx);
      const spike = Math.abs(Math.sin(3 * (a - 0.3)));
      const len = 11 * (1 - spike * 3.2);
      if (d < len) return d < len * 0.5 ? 'w' : spike < 0.08 ? '5' : 'c';
      return d < 4.4 ? '5' : null;
    }
    const [r, len, half] = [
      [6, 7, 2.2],
      [11, 5.5, 1.8],
      [16, 3.5, 1.2],
    ][f - 1] as [number, number, number];
    for (const [k, [a, l]] of SHARDS.entries()) {
      if (f === 3 && k % 2) continue;
      const s = shardAt(x, y, a + f * 0.05, r * l, len, half);
      if (s) return f === 3 ? (s === 'w' ? '5' : '4') : s;
    }
    // A ring of cold light opening behind them.
    return f === 1 && Math.abs(d - 4.5) < 0.7 ? '5' : null;
  });

/**
 * The Glacier's knock-out: a block of ice round the fallen (its frames 0 and 1: whole, then cracked), and the shards
 * it bursts into (2 on: a particle picks one). 32×32, the block 28×22 in it.
 */
const block = (cracked: boolean) => (c: PixelCanvas) =>
  paint(c, (x, y) => {
    // A block in 3/4: its top face (y 5-10, leaning right), its front face below it, corners bevelled.
    const [l, r] = [2 + Math.max(0, 10 - y) * 0.4, 29 - Math.max(0, y - 23)];
    if (y < 5 || y > 27 || x < l || x > r) return null;
    if (cracked) {
      const crack = Math.min(
        fromPath(x, y, [15, 5, 13, 10, 17, 15, 14, 21, 16, 27]),
        fromPath(x, y, [13, 10, 7, 13, 4, 18]),
        fromPath(x, y, [17, 15, 23, 17, 27, 14]),
      );
      if (crack < 0.6) return '2';
      if (crack < 1.3) return 'w';
    }
    if (y < 10) return x < l + 1.2 ? 'w' : y < 7 ? 'w' : '5';
    if (y < 11) return '4';
    // The front: a lit left edge, a glint across it, darker toward its foot.
    if (x < l + 1) return 'w';
    if (x > r - 1.2) return '3';
    const glint = Math.abs(x - y * 0.7 - 2) < 0.8 || Math.abs(x - y * 0.7 - 6) < 0.5;
    if (glint && y < 22) return 'w';
    return y > 25 ? '3' : y > 19 ? '4' : '5';
  });

/** Its shards: a few splinters of ice, each lit along one facet. */
const SPLINTERS = [
  `
  ......w.
  .....w5.
  ....w54.
  ...w54..
  ..w54...
  .w54....
  .54.....
  .4......
  `,
  `
  ...w....
  ..ww5...
  .ww554..
  .w5c54..
  ..554...
  ...4....
  `,
  `
  .w......
  .ww.....
  .w5c....
  .w55c...
  ..5544..
  ...44...
  `,
  `
  ....ww..
  ..ww55..
  .w5c4...
  w554....
  .44.....
  `,
] as const;

const splinter = (src: string) => (c: PixelCanvas) => c.stamp(src, 12, 12);

export default [
  sprite({
    name: 'permafrost_shot',
    ...shot,
    notes: `Permafrost's bullet (ADR-0160 amended): an icicle flying point first, its heart white (the stock pellet's size
round its head), cyan along its top edge and pale grey along its bottom, drawn out long behind, a streak and flakes of
snow trailing it (new flakes each frame). Head right. Rubric (4×): S4 R5 P5 L4 C4 A4 F4. Weakest: on the lane its grey
underside goes; its cyan top and the shot's glow carry it.`,
    anims: { idle: { fps: 15, frames: frames3(icicle) } },
  }),
  sprite({
    name: 'permafrost_muzzle',
    ...muzzle,
    notes: `Permafrost's muzzle flash (ADR-0160 amended): a cone of frost out of the muzzle from a white flash, white at its
heart, cyan at its fringe, breaking into streaks; snowflakes blown off it fly on over 3 frames. Points right. Rubric
(4×): S4 R4 P5 L4 C4 A4 F4. Weakest: its streaks read as a gust more than frost.`,
    anims: { flash: { fps: 30, loop: false, frames: frames3(frostCone) } },
  }),
  sprite({
    name: 'permafrost_burst',
    ...burst,
    notes: `Permafrost's hit (ADR-0160 amended): it shatters, a white star of crystal, then seven shards flung out, shrinking,
the last few glints. 4 frames. Rubric (4×): S4 R4 P5 L4 C4 A4 F4.`,
    anims: { burst: { fps: 20, loop: false, frames: frames4(shatter) } },
  }),
  sprite({
    name: 'glacier_bit',
    group: 'fx',
    size: [32, 32],
    origin: [16, 16],
    density: 4,
    notes: `The Glacier set's knock-out (ADR-0160 amended): frames 0-1 a block of ice in 3/4 (its top face lit, a glint
across its front, darker at its foot), whole then cracked; frames 2-5 the splinters it bursts into (a particle picks one).
The flourish scales the block ×2 over the body lying. Rubric (4×): S4 R4 P5 L4 C4 A- F4. Weakest: scaled ×2 its texels are
half a world px.`,
    anims: {
      idle: { fps: 8, frames: [block(false), block(true), ...SPLINTERS.map(splinter)] },
    },
  }),
];
